import { useSuspenseQuery } from '@tanstack/react-query'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { loadingComponent } from '@omnea-app/fe-legacy/components/shared/Loading/loadingComponent'
import { OrgSettingsHttpClient } from '@omnea-app/org-settings/orgSettingsHttpClient'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { FeatureFlagsContextValue } from './types'
import { useEnrichUserAnalyticsWithFlags } from './useEnrichUserAnalyticsWithFlags'
import { useRecordFeatureFlagsToMeticulous } from './useRecordFeatureFlagsToMeticulous'

export const ALL_FEATURE_FLAGS_OFF: FeatureFlagsContextValue = Object.keys(FeatureFlagsEnum).reduce((acc, featureFlag) => {
  return {
    ...acc,
    [featureFlag]: false,
  }
}, {} as FeatureFlagsContextValue)

// Exported to allow use in tests and Storybook!
export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(ALL_FEATURE_FLAGS_OFF)

export const useFeatureFlags = (): FeatureFlagsContextValue => {
  return useContext(FeatureFlagsContext)
}

const sixHoursMs = 6 * 60 * 60 * 1000

export const GetFeatureFlagsQueryKey = 'GetEnabledOrganisationFeatureFlags'

export const FeatureFlagsProvider: React.FC<PropsWithChildren> = loadingComponent(
  ({ children }) => {
    const { getToken, user } = useAuthenticatedUser()
    const featureFlags = useSuspenseQuery({
      queryKey: [GetFeatureFlagsQueryKey],
      queryFn: async () => {
        const authorization = await getToken()
        const orgSettingsClient = new OrgSettingsHttpClient({ authorization })
        const { data, error } = await orgSettingsClient.GET('/feature-flags')
        throwIfApiError(error)
        return data.featureFlags
      },
      staleTime: sixHoursMs,
    }).data

    const contextValue = useMemo<FeatureFlagsContextValue>(() => {
      return Object.keys(FeatureFlagsEnum).reduce((acc, featureFlag) => {
        return {
          ...acc,
          [featureFlag]: featureFlags?.[featureFlag]?.isOn ?? false,
        }
      }, {} as FeatureFlagsContextValue)
    }, [featureFlags])

    // Enrich Mixpanel user properties with feature flag statuses
    useEnrichUserAnalyticsWithFlags(user.id, contextValue)

    // Record custom feature flags to Meticulous
    useRecordFeatureFlagsToMeticulous(contextValue)

    return <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>
  },
  {
    catchError: false,
  }
)
